/**
 * Custom Hook: useWebhooks
 * Gere l'etat et les operations pour le monitoring des webhooks
 *
 * Migrated to TanStack Query (useQuery)
 * Backward compatible: same hook name + same return interface
 */

import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  webhooksAPI,
  WebhookEvent,
  WebhookEventDetails,
  WebhookStatistics,
  WebhookHealth,
  GetWebhookEventsParams,
} from '../utils/webhooksAPI';
import { useAuth } from './useAuth';
import { logger } from '@/utils/logger';

// ============================================
// QUERY KEYS
// ============================================

export const webhookKeys = {
  all: ['webhooks'] as const,
  events: (params?: GetWebhookEventsParams) =>
    [...webhookKeys.all, 'events', params] as const,
  statistics: (provider?: string, startDate?: string, endDate?: string) =>
    [...webhookKeys.all, 'statistics', provider, startDate, endDate] as const,
  health: () => [...webhookKeys.all, 'health'] as const,
  failed: (limit?: number) => [...webhookKeys.all, 'failed', limit] as const,
  eventDetails: (id: string) => [...webhookKeys.all, 'event-details', id] as const,
};

// ============================================
// INTERFACES (unchanged for backward compatibility)
// ============================================

export interface UseWebhooksOptions {
  /**
   * Charger automatiquement les evenements au montage
   * @default false
   */
  autoLoad?: boolean;

  /**
   * Parametres par defaut pour le chargement automatique
   */
  defaultParams?: GetWebhookEventsParams;
}

export interface UseWebhooksReturn {
  /**
   * Liste des evenements webhooks
   */
  events: WebhookEvent[];

  /**
   * Pagination des evenements
   */
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  } | null;

  /**
   * Statistiques webhooks
   */
  statistics: WebhookStatistics | null;

  /**
   * Etat de sante webhooks
   */
  health: WebhookHealth | null;

  /**
   * Webhooks echoues
   */
  failedWebhooks: WebhookEventDetails[];

  /**
   * Etat de chargement
   */
  loading: boolean;

  /**
   * Message d'erreur
   */
  error: string | null;

  /**
   * Charger les evenements webhooks
   */
  loadEvents: (params?: GetWebhookEventsParams) => Promise<void>;

  /**
   * Charger les statistiques webhooks
   */
  loadStatistics: (
    provider?: 'paypal' | 'stripe',
    startDate?: string,
    endDate?: string
  ) => Promise<void>;

  /**
   * Charger l'etat de sante
   */
  loadHealth: () => Promise<void>;

  /**
   * Charger les webhooks echoues
   */
  loadFailedWebhooks: (limit?: number) => Promise<void>;

  /**
   * Charger les details d'un evenement
   */
  loadEventDetails: (id: string) => Promise<WebhookEventDetails | null>;

  /**
   * Reinitialiser l'etat
   */
  reset: () => void;

  /**
   * Effacer le message d'erreur
   */
  clearError: () => void;
}

/**
 * Hook pour gerer les webhooks
 *
 * @example
 * ```tsx
 * function WebhooksComponent() {
 *   const {
 *     events,
 *     statistics,
 *     health,
 *     loading,
 *     loadEvents,
 *     loadStatistics,
 *     loadHealth,
 *   } = useWebhooks({ autoLoad: true });
 *
 *   return (
 *     <div>
 *       {health && !health.healthy && (
 *         <Alert>Webhook health issues: {health.issues.join(', ')}</Alert>
 *       )}
 *       {events.map(event => (
 *         <div key={event.id}>{event.eventType}</div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useWebhooks(options?: UseWebhooksOptions): UseWebhooksReturn {
  const { autoLoad = false, defaultParams } = options || {};
  const { user } = useAuth();
  const tenantId = user?.tenantId;
  const queryClient = useQueryClient();

  // Local state for imperative parameters
  const [eventParams, setEventParams] = useState<GetWebhookEventsParams | undefined>(defaultParams);
  const [statsConfig, setStatsConfig] = useState<{
    provider?: 'paypal' | 'stripe';
    startDate?: string;
    endDate?: string;
  }>({});
  const [failedLimit, setFailedLimit] = useState<number | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [isReset, setIsReset] = useState(false);

  // ============================================
  // QUERIES
  // ============================================

  /**
   * Events list query
   */
  const eventsQuery = useQuery({
    queryKey: webhookKeys.events(eventParams),
    queryFn: () => webhooksAPI.getEvents(eventParams),
    enabled: !!tenantId && autoLoad && !isReset,
    staleTime: 30 * 1000,
  });

  /**
   * Statistics query
   */
  const statsQuery = useQuery({
    queryKey: webhookKeys.statistics(statsConfig.provider, statsConfig.startDate, statsConfig.endDate),
    queryFn: () => webhooksAPI.getStatistics(statsConfig.provider, statsConfig.startDate, statsConfig.endDate),
    enabled: !!tenantId && autoLoad && !isReset,
    staleTime: 60 * 1000,
  });

  /**
   * Health query
   */
  const healthQuery = useQuery({
    queryKey: webhookKeys.health(),
    queryFn: () => webhooksAPI.getHealth(),
    enabled: !!tenantId && autoLoad && !isReset,
    staleTime: 60 * 1000,
  });

  /**
   * Failed webhooks query (lazy - only fetches when limit is set)
   */
  const failedQuery = useQuery({
    queryKey: webhookKeys.failed(failedLimit),
    queryFn: () => webhooksAPI.getFailedWebhooks(failedLimit || 50),
    enabled: !!tenantId && failedLimit !== undefined && !isReset,
    staleTime: 30 * 1000,
  });

  // ============================================
  // IMPERATIVE ACTION WRAPPERS (backward compat)
  // ============================================

  /**
   * Charger les evenements webhooks
   */
  const loadEvents = useCallback(async (params?: GetWebhookEventsParams) => {
    try {
      setError(null);
      setIsReset(false);
      setEventParams(params);
      await queryClient.fetchQuery({
        queryKey: webhookKeys.events(params),
        queryFn: () => webhooksAPI.getEvents(params),
        staleTime: 0,
      });
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Session expiree. Veuillez vous reconnecter.');
      } else {
        const errorMessage =
          err.response?.data?.error ||
          err.message ||
          'Erreur lors du chargement des evenements webhooks';
        setError(errorMessage);
      }
      logger.error('Error loading webhook events:', err);
    }
  }, [queryClient]);

  /**
   * Charger les statistiques webhooks
   */
  const loadStatistics = useCallback(
    async (provider?: 'paypal' | 'stripe', startDate?: string, endDate?: string) => {
      try {
        setError(null);
        setStatsConfig({ provider, startDate, endDate });
        await queryClient.fetchQuery({
          queryKey: webhookKeys.statistics(provider, startDate, endDate),
          queryFn: () => webhooksAPI.getStatistics(provider, startDate, endDate),
          staleTime: 0,
        });
      } catch (err: any) {
        if (err.response?.status === 401) {
          setError('Session expiree. Veuillez vous reconnecter.');
        } else {
          const errorMessage =
            err.response?.data?.error ||
            err.message ||
            'Erreur lors du chargement des statistiques';
          setError(errorMessage);
        }
        logger.error('Error loading webhook statistics:', err);
      }
    },
    [queryClient]
  );

  /**
   * Charger l'etat de sante webhooks
   */
  const loadHealth = useCallback(async () => {
    try {
      setError(null);
      await queryClient.fetchQuery({
        queryKey: webhookKeys.health(),
        queryFn: () => webhooksAPI.getHealth(),
        staleTime: 0,
      });
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Session expiree. Veuillez vous reconnecter.');
      } else {
        const errorMessage =
          err.response?.data?.error ||
          err.message ||
          'Erreur lors du chargement de l\'etat de sante';
        setError(errorMessage);
      }
      logger.error('Error loading webhook health:', err);
    }
  }, [queryClient]);

  /**
   * Charger les webhooks echoues
   */
  const loadFailedWebhooks = useCallback(async (limit: number = 50) => {
    try {
      setError(null);
      setIsReset(false);
      setFailedLimit(limit);
      await queryClient.fetchQuery({
        queryKey: webhookKeys.failed(limit),
        queryFn: () => webhooksAPI.getFailedWebhooks(limit),
        staleTime: 0,
      });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        'Erreur lors du chargement des webhooks echoues';
      setError(errorMessage);
      logger.error('Error loading failed webhooks:', err);
    }
  }, [queryClient]);

  /**
   * Charger les details d'un evenement
   */
  const loadEventDetails = useCallback(async (id: string): Promise<WebhookEventDetails | null> => {
    try {
      setError(null);
      return await queryClient.fetchQuery({
        queryKey: webhookKeys.eventDetails(id),
        queryFn: () => webhooksAPI.getEventDetails(id),
        staleTime: 60 * 1000,
      });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        'Erreur lors du chargement des details';
      setError(errorMessage);
      logger.error('Error loading event details:', err);
      return null;
    }
  }, [queryClient]);

  /**
   * Reinitialiser l'etat
   */
  const reset = useCallback(() => {
    setEventParams(defaultParams);
    setStatsConfig({});
    setFailedLimit(undefined);
    setError(null);
    setIsReset(true);
    queryClient.removeQueries({ queryKey: webhookKeys.all });
  }, [defaultParams, queryClient]);

  /**
   * Effacer le message d'erreur
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ============================================
  // COMPUTED STATE
  // ============================================

  const loading =
    eventsQuery.isLoading ||
    statsQuery.isLoading ||
    healthQuery.isLoading ||
    failedQuery.isLoading;

  const combinedError =
    error ||
    (eventsQuery.error ? (eventsQuery.error instanceof Error ? eventsQuery.error.message : 'Erreur lors du chargement des evenements') : null) ||
    (statsQuery.error ? (statsQuery.error instanceof Error ? statsQuery.error.message : 'Erreur lors du chargement des statistiques') : null) ||
    (healthQuery.error ? (healthQuery.error instanceof Error ? healthQuery.error.message : 'Erreur lors du chargement de la sante') : null);

  return {
    events: eventsQuery.data?.events || [],
    pagination: eventsQuery.data?.pagination || null,
    statistics: statsQuery.data || null,
    health: healthQuery.data || null,
    failedWebhooks: failedQuery.data || [],
    loading,
    error: combinedError,
    loadEvents,
    loadStatistics,
    loadHealth,
    loadFailedWebhooks,
    loadEventDetails,
    reset,
    clearError,
  };
}
